import React, { useState, useEffect } from 'react';
import { RootState } from '../Redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import axios from 'axios';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Card, Breadcrumb, Pagination } from 'react-bootstrap';
import { faEdit, faEye, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import "./purchase.css";

interface Purchase {
    id: number;
    product: number;
    supplier: number;
    quantity: number;
    purchase_price: string;
    date_purchased: string;
    total_cost: string;
    product_name: string;
    supplier_name: string;
    payment_type: string;
    status: string;
    amount_paid: string;
    balance: string;
    reference_number: string;
    product_image: null;
}

export default function PurchaseComponent() {
    const [purchases, setPurchases] = useState<Purchase[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'view' | 'edit'>('view');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [error, setError] = useState<string | null>(null); // State for error handling

    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth.value);

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    };

    const fetchData = async () => {
        try {
            const token = getCookie('token');
            if (!token) {
                dispatch(setAuth(false));
                return;
            }
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const userResponse = await axios.get('https://api.sominnovations.xyz/users/user', {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setUserRole(userResponse.data.user.role); // Set the user role
            dispatch(setAuth(true));
        } catch (e) {
            dispatch(setAuth(false));
        }
    };

    const fetchPurchases = async (page: number) => {
        setLoading(true);
        try {
            const response = await axios.get(`https://api.sominnovations.xyz/Branches/purchase/?page=${page}&search=${searchTerm}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setPurchases(response.data.results);
            setTotalPages(Math.ceil(response.data.count / 20));
        } catch (error) {
            console.error('Error fetching purchases:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [dispatch]);

    useEffect(() => {
        if (auth) {
            fetchPurchases(currentPage);
        }
    }, [auth, currentPage, searchTerm]);

    const handleEdit = (purchase: Purchase) => {
        setSelectedPurchase(purchase);
        setModalType('edit');
        setShowModal(true);
        setError(null); // Clear any previous errors
    };

    const handleView = (purchase: Purchase) => {
        setSelectedPurchase(purchase);
        setModalType('view');
        setShowModal(true);
    };

    const handleSaveEdit = async () => {
        if (selectedPurchase) {
            try {
                const response = await axios.patch(
                    `https://api.sominnovations.xyz/Branches/purchase/${selectedPurchase.id}/`,
                    {
                        quantity: selectedPurchase.quantity,
                        purchase_price: selectedPurchase.purchase_price,
                        // Include other fields you want to edit
                    }
                );
                // Update the local state with the edited purchase
                setPurchases(purchases.map(p =>
                    p.id === selectedPurchase.id ? { ...p, ...response.data } : p
                ));
                setShowModal(false); // Close the modal
            } catch (error) {
                console.error('Error updating purchase:', error);
                setError('Failed to update purchase. Please try again.'); // Set error message
            }
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm("Are you sure you want to delete this purchase?")) {
            try {
                await axios.delete(`https://api.sominnovations.xyz/Branches/purchase/${id}/`);
                setPurchases(purchases.filter(purchase => purchase.id !== id));
                console.log('Deleted purchase with ID:', id);
            } catch (error) {
                console.error('Error deleting purchase:', error);
            }
        }
    };

    return (
        <div className="container purchase-product" style={{ padding: "20px" }}>
            <Card className="mb-4" style={{ height: "150px" }}>
                <Card.Header style={{ backgroundColor: '#5271ff', color: '#fff', height: "50px" }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>Purchases</h4>
                        <Breadcrumb>
                            <Breadcrumb.Item href="dashboard"> <p style={{ color: "white", textDecoration: "none" }}> Dashboard</p> </Breadcrumb.Item>
                            <Breadcrumb.Item active>Purchases</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="mb-4">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search Purchases..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <Link to="/add-purchase">
                        <Button variant="success">+ Add Purchase</Button>
                    </Link>
                </Card.Body>
            </Card>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <>
                    <div className="table-responsive-sm">
                        <table className="table table-hover table-responsive-sm text-center">
                            <thead className="table-primary text-dark">
                                <tr>
                                    <th>Reference</th>
                                    <th>Supplier</th>
                                    <th>Product</th>
                                    <th>Status</th>
                                    <th>Grand Total</th>
                                    <th>Payment Type</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {purchases.map((purchase) => (
                                    <tr key={purchase.reference_number} style={{ backgroundColor: '#f9f9f9' }} className='trow'>
                                        <td>
                                            <span
                                                style={{
                                                    backgroundColor: '#FFD2D2',
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    color: '#D9534F',
                                                    fontWeight: 'bold',
                                                    fontSize: '16px'
                                                }}
                                            >
                                                {purchase.reference_number}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '13px' }}>{purchase.supplier_name}</td>
                                        <td style={{ fontSize: '13px' }}>{purchase.product_name}</td>
                                        <td>
                                            <span
                                                style={{
                                                    backgroundColor: purchase.status === 'Paid' ? '#D4EDDA' : '#FFF3CD',
                                                    color: purchase.status === 'Paid' ? '#155724' : '#856404',
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    fontWeight: '500',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                {purchase.status}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '13px' }}>{purchase.total_cost}</td>
                                        <td>
                                            <span
                                                style={{
                                                    backgroundColor: purchase.payment_type === 'cash' ? '#CCE5FF' : '#E2E3E5',
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    color: purchase.payment_type === 'cash' ? '#004085' : '#383D41',
                                                    fontWeight: 'bold',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                {purchase.payment_type.charAt(0).toUpperCase() + purchase.payment_type.slice(1)}
                                            </span>
                                        </td>
                                        <td className="d-flex justify-content-center align-items-center">
                                            <Button
                                                variant="outline-info"
                                                size="sm"
                                                className="me-1 p-1"
                                                onClick={() => handleView(purchase)}
                                                style={{ width: '28px', height: '28px' }}
                                                title="View"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </Button>
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="me-1 p-1"
                                                onClick={() => handleEdit(purchase)}
                                                style={{ width: '28px', height: '28px' }}
                                                title="Edit"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </Button>
                                            {/* Conditionally render the delete button */}
                                            {userRole === 'admin' && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="p-1"
                                                    onClick={() => handleDelete(purchase.id)}
                                                    style={{ width: '28px', height: '28px' }}
                                                    title="Delete"
                                                >
                                                    <FontAwesomeIcon icon={faTrashAlt} />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination>
                        {Array.from({ length: totalPages }).map((_, i) => (
                            <Pagination.Item key={i} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                                {i + 1}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </>
            )}

            {selectedPurchase && (
                <Modal show={showModal} onHide={() => setShowModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{modalType === 'view' ? 'Purchase Details' : 'Edit Purchase'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalType === 'view' ? (
                            <div>
                                {selectedPurchase.product_image ? (
                                    <img
                                        src={`http://192.168.0.113:8000${selectedPurchase.product_image}`}
                                        alt={selectedPurchase.product_name}
                                        style={{ width: '100%', height: 'auto', marginBottom: '15px' }}
                                    />
                                ) : (
                                    <p>No image available</p>
                                )}
                                <p><strong>Reference Number:</strong> {selectedPurchase.reference_number}</p>
                                <p><strong>Supplier:</strong> {selectedPurchase.supplier_name}</p>
                                <p><strong>Product:</strong> {selectedPurchase.product_name}</p>
                                <p><strong>Quantity:</strong> {selectedPurchase.quantity}</p>
                                <p><strong>Purchase Price:</strong> {selectedPurchase.purchase_price}</p>
                                <p><strong>Date Purchased:</strong> {selectedPurchase.date_purchased}</p>
                                <p><strong>Total Cost:</strong> {selectedPurchase.total_cost}</p>
                                <p><strong>Status:</strong> {selectedPurchase.status}</p>
                                <p><strong>Payment Type:</strong> {selectedPurchase.payment_type}</p>
                                <p><strong>Amount Paid:</strong> {selectedPurchase.amount_paid}</p>
                                <p><strong>Balance:</strong> {selectedPurchase.balance}</p>
                            </div>
                        ) : (
                            <form>
                                <div className="form-group">
                                    <label htmlFor="quantity">Quantity</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={selectedPurchase.quantity}
                                        onChange={(e) =>
                                            setSelectedPurchase({ ...selectedPurchase, quantity: parseInt(e.target.value, 10) })
                                        }
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="purchasePrice">Purchase Price</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={selectedPurchase.purchase_price}
                                        onChange={(e) =>
                                            setSelectedPurchase({ ...selectedPurchase, purchase_price: e.target.value })
                                        }
                                    />
                                </div>
                                {error && <div className="text-danger mb-3">{error}</div>}
                            </form>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {modalType === 'edit' ? (
                            <Button variant="primary" onClick={handleSaveEdit}>
                                Save Changes
                            </Button>
                        ) : null}
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}